import { useMemo } from "react";

import clsx from "clsx";
import { groupBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { TileType, type TileDescriptor } from "@features/overview-v2/types";
import {
  UniversalBenchmarksIndicator,
  useUniversalBenchmarks,
  type BenchmarkIndicatorRow,
} from "@features/universal-benchmarks";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { PLATFORM_NORTHBEAM } from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { ChangePill } from "../../../../components/v2/change-pill/change-pill";

import { generateLegendData, type LegendItem } from "./utils";

interface Props {
  tile: TileDescriptor;
  tileWidth: number;
  tileHeight: number;
  metric: SalesPageMetric;
  data: GetOverviewMetricsReport_me_overviewMetricsReport;
  compareData?: GetOverviewMetricsReport_me_overviewMetricsReport;
}

export const Legend = ({
  tile,
  tileWidth,
  tileHeight,
  metric,
  data,
  compareData,
}: Props) => {
  const legend = useMemo(
    () => generateLegendData(tile, metric, data),
    [tile, metric, data],
  );

  const compareLegend = useMemo(() => {
    if (!compareData) {
      return null;
    }

    return groupBy(generateLegendData(tile, metric, compareData), "label");
  }, [tile, metric, compareData]);

  return (
    <div
      className={clsx({
        "pb-4": tile.type !== TileType.METRIC,
        "pt-4": tileWidth !== 1,
      })}
    >
      {legend.map((row) => (
        <LegendRow
          key={row.label}
          row={row}
          tile={tile}
          tileWidth={tileWidth}
          tileHeight={tileHeight}
          metric={metric}
          compare={
            compareLegend
              ? compareLegend[row.label]
                ? compareLegend[row.label][0]
                : null
              : null
          }
        />
      ))}
    </div>
  );
};

const LegendRow = ({
  row,
  tile,
  tileWidth,
  tileHeight,
  metric,
  compare,
}: {
  row: LegendItem;
  tile: TileDescriptor;
  tileWidth: number;
  tileHeight: number;
  metric: SalesPageMetric;
  compare: LegendItem | null;
}) => {
  const { designElementManager } = useDesignElementManager();
  const { filters: pageFilters } = useOverviewContext();

  const benchmark = useUniversalBenchmarks({
    level: pageFilters.level,
    attributionModel: pageFilters.attributionModel,
    attributionWindow: pageFilters.attributionWindow,
    accountingMode: pageFilters.accountingMode,
    breakdowns: row.breakdowns,
    filters: tile.filters,
    metric: { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv },
  });

  const benchmarkIndicatorRow = useMemo(() => {
    if (!benchmark || !metric || benchmark?.status === "loading") {
      return null;
    }

    return {
      name:
        metric.name ??
        metric.metadata.name ??
        metric.spec?.name ??
        metric.metadata.salesTableMetricId,
      current: row.value,
      target: benchmark.value,
      format: metric.format,
      positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
    } as BenchmarkIndicatorRow;
  }, [benchmark, row.value, metric]);

  const isExpandedMetricTile =
    tileHeight === 2 && tile.type === TileType.METRIC;

  const shouldWrap = isExpandedMetricTile || tileWidth !== 1;

  const isPlatformBreakdown =
    tile.breakdowns.length === 1 && tile.breakdowns[0] === PLATFORM_NORTHBEAM;

  const changeValue = compare
    ? (row.value - compare.value) / compare.value
    : null;

  let icon = null;

  if (isPlatformBreakdown) {
    const designElement = designElementManager.getDesignElement(row.label);

    icon = designElement.getComponent({
      borderColor: "#C1C5CD",
      size: "18",
    });
  }

  return (
    <div
      className={clsx("text-sm", {
        "flex items-center mb-1 text-v2-primary": !shouldWrap,
        "mb-5": shouldWrap,
      })}
    >
      <div
        className={clsx("flex items-center gap-1 truncate", {
          "mb-2 max-w-[160px]": shouldWrap,
        })}
      >
        {icon ? (
          <div className="shrink-0 relative">
            {icon}
            <span
              className={clsx(
                "absolute right-[-1px] bottom-[-1px]",
                "inline-block w-[7px] h-[7px] rounded-sm",
              )}
              style={{ backgroundColor: row.color }}
            ></span>
          </div>
        ) : (
          <span
            className="shrink-0 inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: row.color }}
          ></span>
        )}
        <GenericTooltip content={row.label} noInfoCircle className="truncate">
          {row.label}
        </GenericTooltip>
      </div>
      {!shouldWrap && (
        <div className="flex-1 shrink-0 flex items-center">
          <div
            className={clsx(
              "flex-1 border-b border-v2-secondary border-dashed mx-2 min-w-[18px]",
            )}
          ></div>
        </div>
      )}
      <div className="shrink-0 flex items-center">
        <MetricValue
          as="div"
          className={clsx("shrink-0 text-cosmic", {
            "text-[18px]": !shouldWrap,
            "text-[24px]": shouldWrap,
          })}
          format={metric.format}
          value={row.value}
          precision="approximate"
        />
        {benchmark?.status === "loading" && (
          <div className="flex items-center ml-1">
            <Spinner colorClassname="w-3 h-3 text-cosmic text-xxs" />
          </div>
        )}
        {benchmarkIndicatorRow !== null && (
          <UniversalBenchmarksIndicator
            benchmarkIndicatorRows={[benchmarkIndicatorRow]}
          />
        )}
        {changeValue !== null && (
          <div className="shrink-0 text-right">
            <div className="min-w-[56px]">
              <ChangePill
                value={changeValue}
                isPositiveChangeGood={metric.isPositiveChangeGood}
                size="md"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
